import styled from 'styled-components';

import type { JobBookmarkState } from '@xing-com/crate-common-graphql-types';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { ApplyButton } from '@xing-com/crate-jobs-components-apply-button';
import type { JobDetails, ApplicationData } from '@xing-com/crate-jobs-helpers';
import { useBreakpoints } from '@xing-com/crate-jobs-hooks';
import { OnlyOnSmall } from '@xing-com/layout-primitives';
import { mediaConfined } from '@xing-com/layout-tokens';
import { space3XL, spaceS, spaceXXL } from '@xing-com/tokens';

import { ShareJobProvider } from '../../../hooks';

import { AlertButton } from './alert-button';
import { BookmarkButton } from './bookmark-button';
import { MoreMenuButton } from './more-menu-button';
import { useMainActions } from './use-main-actions';

const ActionsContainer = styled.div<{ $equalSize: boolean }>`
  display: flex;
  flex-direction: row;
  gap: ${spaceS};
  width: auto;
  margin-top: ${spaceXXL};

  @media ${mediaConfined} {
    padding: 0;
    flex-direction: row;
    margin-top: ${space3XL};
  }

  @media print {
    display: none;
  }

  @media ${mediaConfined} {
    > * {
      flex-basis: ${({ $equalSize }) =>
        $equalSize ? '100% !important' : '23%'};
    }
  }
`;

const StyledApplyButton = styled(ApplyButton)`
  width: 100%;

  @media ${mediaConfined} {
    flex-basis: 46%;
  }
`;

type Props = {
  application: ApplicationData;
  bookmarkState?: JobBookmarkState;
  job: JobDetails;
};

export const MainActions: React.FC<Props> = ({
  application,
  job,
  bookmarkState,
}) => {
  const { isLoggedIn } = useLoginState();
  const { showApplyButton, showBookmarkButton, showAlertButton } =
    useMainActions({
      application,
      job,
    });
  const { isMediaSmallOrTiny } = useBreakpoints();

  return showApplyButton || showBookmarkButton ? (
    <ActionsContainer $equalSize={showAlertButton}>
      {showApplyButton ? (
        <StyledApplyButton application={application} variant="primary" />
      ) : null}
      {showBookmarkButton ? (
        <BookmarkButton
          job={job}
          bookmarkState={bookmarkState}
          iconOnly={showApplyButton && isMediaSmallOrTiny}
        />
      ) : null}
      {showAlertButton ? (
        <AlertButton
          job={job}
          iconOnly={showApplyButton && isMediaSmallOrTiny}
        />
      ) : null}
      {isLoggedIn ? (
        <OnlyOnSmall>
          <ShareJobProvider globalId={job.globalId} url={job.url}>
            <MoreMenuButton job={job} />
          </ShareJobProvider>
        </OnlyOnSmall>
      ) : null}
    </ActionsContainer>
  ) : null;
};
